import jsPDF from "jspdf";

export const DEFAULT_PANEL_DURATION = 2.5; // segundos, usado por el reproductor

// Da un id estable (y una duración por defecto) a páginas/panels que no lo
// tengan. Cubre proyectos exportados antes de agregar estos campos, o
// archivos .frameflow hechos/editados a mano.
export const normalizeStoryboard = (data) => {
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map((page, pIndex) => ({
    ...page,
    id: page.id || `page-${Date.now()}-${pIndex}`,
    page: pIndex + 1,
    panels: Array.isArray(page.panels)
      ? page.panels.map((panel, panelIndex) => ({
          ...panel,
          id: panel.id || `${Date.now()}-${pIndex}-${panelIndex}`,
          duration:
            typeof panel.duration === "number" && panel.duration > 0
              ? panel.duration
              : DEFAULT_PANEL_DURATION,
        }))
      : [],
  }));
};

// Exporta el storyboard completo a un PDF (2 columnas, proporción 16:9).
export const exportStoryboardPDF = (storyboard) => {
  const pdf = new jsPDF("p", "mm", "a4");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const marginX = 15;
  const marginY = 20;
  const gap = 10;
  const colWidth = (pageWidth - 2 * marginX - gap) / 2;
  const panelHeight = (colWidth * 9) / 16 + 20;

  let y = marginY;
  let col = 0;
  let frameCounter = 1;

  for (let pg of storyboard) {
    pdf.setFontSize(14);
    pdf.text(pg.title, marginX, y, { maxWidth: pageWidth - 2 * marginX });
    y += 8;

    pdf.setFontSize(10);
    const descLines = pdf.splitTextToSize(
      pg.description,
      pageWidth - 2 * marginX
    );
    pdf.text(descLines, marginX, y);
    y += descLines.length * 5 + 5;

    for (let panel of pg.panels) {
      const posX = col === 0 ? marginX : marginX + colWidth + gap;

      pdf.setFontSize(8);
      pdf.text(`#${frameCounter}`, posX, y - 2);

      const imageH = (colWidth * 9) / 16;
      pdf.rect(posX, y, colWidth, imageH + 20);

      if (panel.image && panel.image.startsWith("data:image")) {
        pdf.addImage(panel.image, "JPEG", posX, y, colWidth, imageH);
      } else {
        pdf.text("❌ Imagen no disponible", posX + 5, y + 25);
      }

      pdf.setFontSize(9);
      const textLines = pdf.splitTextToSize(panel.text, colWidth - 4);
      pdf.text(textLines, posX + 2, y + imageH + 8);

      frameCounter++;

      if (col === 0) {
        col = 1;
      } else {
        col = 0;
        y += imageH + 30;
      }

      if (y + panelHeight > pageHeight - marginY) {
        pdf.addPage();
        y = marginY;
        col = 0;
      }
    }

    pdf.addPage();
    y = marginY;
    col = 0;
  }

  pdf.save("storyboard.pdf");
};

// Exporta el storyboard a un archivo .frameflow (JSON).
export const exportStoryboardProject = (storyboard) => {
  let frameCounter = 1;
  const storyboardWithFrames = storyboard.map((page) => ({
    ...page,
    panels: page.panels.map((panel) => ({
      ...panel,
      frameNumber: frameCounter++,
    })),
  }));

  const dataStr = JSON.stringify(storyboardWithFrames, null, 2);
  const blob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "storyboard.frameflow";
  a.click();

  URL.revokeObjectURL(url);
};

// Lee y normaliza un archivo .frameflow/.json. Llama a onSuccess(storyboard)
// o onError(message).
export const importStoryboardProject = (file, onSuccess, onError) => {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);
      const normalized = normalizeStoryboard(data);
      if (!normalized) throw new Error("Formato inválido");
      onSuccess(normalized);
    } catch (err) {
      onError("Error: el archivo no es válido");
    }
  };
  reader.readAsText(file);
};
